import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppShell } from '../components/AppShell';
import { EntrepriseManagerShared } from '../components/EntrepriseManagerShared';
import { ChantiersCSPSFormSheet } from '../components/chantierscsps/ChantiersCSPSFormSheet';
import { DossierSheet } from '../components/dossier/DossierSheet';
import { LotsManagerSheet } from '../components/lots/LotsManagerSheet';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Plus, MapPin, User, MagnifyingGlass,
  PencilSimple, Trash, ArrowLeft, ShieldCheck, HardHat, UsersThree,
  Notebook, FolderOpen, Stack,
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getPhaseDisplay } from '../constants/btpPhases';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EMPTY_FORM = {
  name: '', address: '', client_name: '', maitre_oeuvre: '',
  start_date: '', end_date: '', status: 'active', description: '',
  type: 'csps', entreprises: [], phase_chantier: 'realisation', niveau_csps: 2,
  coordonnateur_sps: '', nature_travaux: '', duree_previsionnelle: '',
  acces_chantier: '', circulation: '', zones_stockage: '', installations_chantier: '',
  risques_principaux: '', contraintes_specifiques: '', numéro_operation: '', reference_dossier: ''
};

export function ChantiersCSPSPage() {
  const navigate = useNavigate();
  const [chantiers, setChantiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [editingChantier, setEditingChantier] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [expandedCSPSChantier, setExpandedCSPSChantier] = useState(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [submitting, setSubmitting] = useState(false);
  const [dossierChantier, setDossierChantier] = useState(null);
  const [lotsChantier, setLotsChantier] = useState(null);

  const fetchChantiers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chantiers?type=csps`, { withCredentials: true });
      setChantiers((res.data || []).filter(c => c && c.id));
    } catch (_err) { setChantiers([]); }
    finally { setLoading(false); }
  }, [setChantiers, setLoading]);

  useEffect(() => { fetchChantiers(); }, [fetchChantiers]);

  const openCreateSheet = () => {
    setEditingChantier(null); setFormData({ ...EMPTY_FORM }); setIsSheetOpen(true);
  };

  const openEditSheet = (c) => {
    setEditingChantier(c);
    setFormData({
      name: c.name || '', address: c.address || '', client_name: c.client_name || '',
      maitre_oeuvre: c.maitre_oeuvre || '', start_date: c.start_date || '',
      end_date: c.end_date || '', status: c.status || 'active',
      description: c.description || '', type: 'csps',
      entreprises: c.entreprises || [], phase_chantier: c.phase_chantier || 'realisation',
      niveau_csps: c.niveau_csps || 2, coordonnateur_sps: c.coordonnateur_sps || '',
      nature_travaux: c.nature_travaux || '', duree_previsionnelle: c.duree_previsionnelle || '',
      acces_chantier: c.acces_chantier || '', circulation: c.circulation || '',
      zones_stockage: c.zones_stockage || '', installations_chantier: c.installations_chantier || '',
      risques_principaux: c.risques_principaux || '', contraintes_specifiques: c.contraintes_specifiques || '',
      numéro_operation: c.numéro_operation || '', reference_dossier: c.reference_dossier || '',
    });
    setIsSheetOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...formData, niveau_csps: parseInt(formData.niveau_csps) };
      if (editingChantier) {
        await axios.put(`${API}/chantiers/${editingChantier.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/chantiers`, payload, { withCredentials: true });
      }
      setIsSheetOpen(false); setEditingChantier(null); fetchChantiers();
    } catch (_err) { toast.error('Erreur sauvegarde chantier'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/chantiers/${id}`, { withCredentials: true });
      setDeleteConfirm(null); fetchChantiers();
    } catch (_err) { toast.error('Erreur suppression chantier'); }
  };

  const getPhaseBadge = (phase) => {
    const d = getPhaseDisplay(phase);
    const slug = (phase || 'na').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    return <span className={`chantix-badge ${d.cls}`} data-testid={`phase-badge-${slug}`}>{d.label}</span>;
  };

  const getNiveauBadge = (n) => {
    const colors = { 1: 'bg-red-100 text-red-700', 2: 'bg-amber-100 text-amber-700', 3: 'bg-emerald-100 text-emerald-700' };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${colors[n] || 'bg-gray-100 text-gray-600'}`}>
        Niveau {n || '?'}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const map = { active: 'chantix-badge-success', completed: 'chantix-badge-info', paused: 'chantix-badge-warning' };
    const labels = { active: 'Actif', completed: 'Terminé', paused: 'En pause' };
    return <span className={`chantix-badge ${map[status] || 'chantix-badge-neutral'}`}>{labels[status] || status}</span>;
  };

  const filtered = (chantiers || []).filter(c => {
    if (!c) return false;
    const s = searchTerm.toLowerCase();
    return (c.name || '').toLowerCase().includes(s) ||
      (c.client_name || '').toLowerCase().includes(s) ||
      (c.address || '').toLowerCase().includes(s) ||
      (c.maitre_oeuvre || '').toLowerCase().includes(s);
  });

  if (loading) {
    return <AppShell><div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-10 h-10 animate-spin text-[#FF6B00]" /></div></AppShell>;
  }

  return (
    <AppShell>
      <div className="space-y-6" data-testid="csps-chantiers-page">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" className="h-10 w-10 md:hidden"
              onClick={() => navigate('/dashboard')} data-testid="back-btn">
              <ArrowLeft size={22} />
            </Button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-[#111827]" data-testid="csps-chantiers-title">Chantiers CSPS</h1>
              <p className="text-[#4B5563] mt-1">{chantiers.length} chantier(s) CSPS</p>
            </div>
          </div>
          <Button onClick={openCreateSheet} className="bg-[#FF6B00] hover:bg-orange-700" data-testid="new-csps-chantier-btn">
            <Plus size={20} className="mr-2" /> Nouveau chantier CSPS
          </Button>
        </div>

        <div className="relative">
          <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5563]" />
          <Input placeholder="Rechercher un chantier CSPS..." value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 h-14"
            data-testid="csps-chantier-search" />
        </div>

        <div className="grid gap-4">
          {filtered.length > 0 ? filtered.map((c) => (
            <Card key={c.id} className="chantix-card border-l-4 border-l-[#FF6B00]" data-testid={`csps-chantier-card-${c.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1 min-w-0">
                    <div className="p-3 rounded-lg bg-orange-100 flex-shrink-0">
                      <ShieldCheck size={24} className="text-[#FF6B00]" weight="fill" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-bold text-[#111827] truncate">{c.name}</h3>
                        {getStatusBadge(c.status)}
                        {getNiveauBadge(c.niveau_csps)}
                        {getPhaseBadge(c.phase_chantier)}
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-[#4B5563] flex items-center gap-2">
                          <MapPin size={16} className="flex-shrink-0" />
                          <span className="truncate">{c.address || 'Adresse non renseignee'}</span>
                        </p>
                        <p className="text-sm text-[#4B5563] flex items-center gap-2">
                          <User size={16} className="flex-shrink-0" />
                          <span className="truncate">MO: {c.client_name || 'Non renseigne'}</span>
                        </p>
                        {c.maitre_oeuvre && (
                          <p className="text-sm text-[#4B5563] flex items-center gap-2">
                            <HardHat size={16} className="flex-shrink-0" />
                            <span className="truncate">MOE: {c.maitre_oeuvre}</span>
                          </p>
                        )}
                        {c.coordonnateur_sps && (
                          <p className="text-sm text-[#4B5563] flex items-center gap-2">
                            <ShieldCheck size={16} className="flex-shrink-0" />
                            <span className="truncate">CSPS: {c.coordonnateur_sps}</span>
                          </p>
                        )}
                        {c.entreprises?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {c.entreprises.slice(0, 3).map((e, i) => (
                              <span key={`ent-${e}-${i}`} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{e}</span>
                            ))}
                            {c.entreprises.length > 3 && (
                              <span className="text-xs text-[#4B5563]">+{c.entreprises.length - 3}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button variant="outline" size="icon"
                      onClick={() => setLotsChantier(c)}
                      className="h-12 w-12 text-[#FF6B00] hover:bg-orange-50"
                      data-testid={`lots-csps-chantier-${c.id}`} title={`Lots (${(c.lot_ids || []).length})`}>
                      <Stack size={20} />
                    </Button>
                    <Button variant="outline" size="icon"
                      onClick={() => setDossierChantier(c)}
                      className="h-12 w-12 text-[#FF6B00] hover:bg-orange-50"
                      data-testid={`dossier-csps-chantier-${c.id}`} title="Dossier documentaire">
                      <FolderOpen size={20} />
                    </Button>
                    <Button variant="outline" size="icon"
                      onClick={() => navigate(`/chantiers/${c.id}/journal`)}
                      className="h-12 w-12 text-[#FF6B00] hover:bg-orange-50"
                      data-testid={`journal-csps-chantier-${c.id}`} title="Journal de chantier">
                      <Notebook size={20} />
                    </Button>
                    <Button variant="outline" size="icon"
                      onClick={() => navigate(`/chantiers-csps/${c.id}/boris`)}
                      className="h-12 w-12 hover:bg-amber-50 border-amber-300 relative overflow-hidden p-0"
                      data-testid={`boris-csps-chantier-${c.id}`} title="Gaby · Expert IA CSPS">
                      <img
                        src="https://customer-assets.emergentagent.com/job_chantier-mobile-1/artifacts/01h3kcfg_1000021463.jpg"
                        alt="Gaby"
                        className="w-full h-full object-cover"
                      />
                      <span className="absolute -top-1 -right-1 text-[8px] bg-amber-500 text-white px-1 rounded-full font-bold leading-tight">β</span>
                    </Button>
                    <Button variant="outline" size="icon"
                      onClick={() => setExpandedCSPSChantier(expandedCSPSChantier === c.id ? null : c.id)}
                      className={`h-12 w-12 ${expandedCSPSChantier === c.id ? 'bg-orange-50 border-orange-300' : ''}`}
                      data-testid={`csps-entreprises-toggle-${c.id}`} title="Entreprises intervenantes">
                      <UsersThree size={20} />
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => openEditSheet(c)}
                      className="h-12 w-12" data-testid={`edit-csps-chantier-${c.id}`}>
                      <PencilSimple size={20} />
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => setDeleteConfirm(c)}
                      className="h-12 w-12 text-red-600 hover:bg-red-50" data-testid={`delete-csps-chantier-${c.id}`}>
                      <Trash size={20} />
                    </Button>
                  </div>
                </div>
                {expandedCSPSChantier === c.id && (
                  <div className="mt-3 pt-3 border-t border-[#FDBA74]">
                    <div className="flex items-center gap-2 mb-2">
                      <UsersThree size={16} className="text-[#FF6B00]" />
                      <span className="text-sm font-medium text-[#111827]">Entreprises intervenantes</span>
                    </div>
                    <EntrepriseManagerShared chantierId={c.id} variant="csps" />
                  </div>
                )}
              </CardContent>
            </Card>
          )) : (
            <Card className="chantix-card">
              <CardContent className="py-12 text-center">
                <ShieldCheck size={48} className="mx-auto mb-4 text-[#FF6B00] opacity-50" />
                <h3 className="font-semibold text-[#111827] mb-2">
                  {searchTerm ? 'Aucun resultat' : 'Aucun chantier CSPS'}
                </h3>
                <p className="text-[#4B5563] mb-4">
                  {searchTerm ? 'Modifiéz votre recherche' : 'Creez votre premier chantier CSPS'}
                </p>
                {!searchTerm && (
                  <Button className="bg-[#FF6B00] hover:bg-orange-700" onClick={openCreateSheet}>
                    <Plus size={20} className="mr-2" /> Créer un chantier CSPS
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <ChantiersCSPSFormSheet
        open={isSheetOpen} onOpenChange={setIsSheetOpen}
        editingChantier={editingChantier}
        formData={formData} setFormData={setFormData}
        submitting={submitting} onSubmit={handleSubmit}
      />

      <DossierSheet
        open={!!dossierChantier}
        onOpenChange={(v) => { if (!v) setDossierChantier(null); }}
        chantier={dossierChantier}
      />

      <LotsManagerSheet
        open={!!lotsChantier}
        onOpenChange={(v) => { if (!v) setLotsChantier(null); }}
        chantier={lotsChantier}
        onChange={fetchChantiers}
      />

      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimér le chantier CSPS ?</AlertDialogTitle>
            <AlertDialogDescription>
              Etes-vous sur de vouloir supprimér "{deleteConfirm?.name}" ? Cette action suppriméra aussi les visites et documents associes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="h-12">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteConfirm?.id)}
              className="h-12 bg-red-600 hover:bg-red-700">Supprimér</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
